import type { Page, PageContentModel } from '@/types/page';
import type { PageSection } from '@/types/page-section';

function getMainSectionById(page: Page, sectionId?: string): PageSection | null {
  if (!sectionId) return null;

  return page.slots?.main.sections.find((section) => section.id === sectionId) ?? null;
}

function normalizeSectionContentModel(sectionContentModel: unknown) {
  if (
    !sectionContentModel ||
    typeof sectionContentModel !== 'object' ||
    Array.isArray(sectionContentModel)
  ) {
    return null;
  }

  const { editorPublishAt: _editorPublishAt, ...contentModel } = sectionContentModel as NonNullable<
    Page['contentModel']
  >;

  return contentModel;
}

type NamedContentModelItem = { id?: string | null; name?: string | null };

const NAMED_COLLECTION_KEYS = [
  'authors',
  'topics',
  'attorneys',
  'courts',
  'practiceAreas',
] as const;
type NamedCollectionKey = (typeof NAMED_COLLECTION_KEYS)[number];

// A name is all rendering needs: a proposed member has no id until it is accepted, while a
// legacy id-only entry has no name and still defers to the page-level list.
function renderableMembers<T extends NamedContentModelItem>(values: T[] | undefined): T[] {
  return Array.isArray(values) ? values.filter((item): item is T => Boolean(item?.name)) : [];
}

function pickSectionNamedCollection<T extends NamedContentModelItem>(
  pageValues: T[] | undefined,
  sectionValues: T[] | undefined,
): T[] | undefined {
  const sectionList = renderableMembers(sectionValues);
  if (sectionList.length > 0) return sectionList;

  // An explicitly emptied section list is a real value, not a gap for the page list to fill.
  if (Array.isArray(sectionValues) && sectionValues.length === 0) return [];

  const pageList = renderableMembers(pageValues);
  if (pageList.length > 0) return pageList;

  return undefined;
}

export function resolvePreviewContentModel(page: Page | null) {
  if (!page) return null;

  return page.contentModel ?? null;
}

export function resolveDraftContentModel(page: Page | null, sectionId?: string) {
  if (!page) return null;

  const sectionContentModel = getMainSectionById(page, sectionId)?.props?.contentModel;
  const normalizedSectionContentModel = normalizeSectionContentModel(sectionContentModel);

  if (!normalizedSectionContentModel) return page.contentModel ?? null;

  const pageContentModel = page.contentModel ?? {};
  const merged = {
    ...pageContentModel,
    ...normalizedSectionContentModel,
  };

  // The page-level list wins only when the section carries nothing renderable, e.g. legacy id-only props.
  const namedCollections: Partial<Record<NamedCollectionKey, NamedContentModelItem[]>> = {};
  for (const key of NAMED_COLLECTION_KEYS) {
    const picked = pickSectionNamedCollection<NamedContentModelItem>(
      pageContentModel[key],
      normalizedSectionContentModel[key],
    );
    if (picked) namedCollections[key] = picked;
  }

  return { ...merged, ...namedCollections } as PageContentModel;
}
